import { useEffect, useMemo, useState } from "react"
import LoadingState from "Components/organisms/loading-state"
import SearchSelect from "Components/molecule/inputs/search-select"
import ExpenseBudgetChart from "Components/molecule/charts/expense-budget-chart"
import ExpenseBudgetVendorChart from "Components/molecule/charts/expense-budget-vendor-chart"
import { ExpenseBudget, PlanVsActual, PlanView } from "models/expense-budget"
import { getExpenseBudgetPlanVsActual } from "slices/finance/expense-budget/thunk"
import { sumAmounts } from "utils/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"

interface SummaryTabProps {
    budget: ExpenseBudget
}

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const ALL = ""
const VENDOR_PREFIX = "vendor:"
const LINE_PREFIX = "line:"

// Billed amount of the lines in month i: shown once the month is closed, or earlier if bills are already linked.
const billedOf = (planVsActual: PlanVsActual, lines: { billed: number[] }[], i: number): number | null => {
    const billed = sumAmounts(lines.map(line => line.billed[i]))
    return planVsActual.periods[i] < planVsActual.firstOpenPeriod || billed !== 0 ? billed : null
}

const SummaryCard =({ label, value, tone }: { label: string; value: string; tone?: "danger" | "success" }) => (
    <div className="section-container" style={{ flex: "1 1 160px" }}>
        <div className="text-muted">{label}</div>
        <h4 className={tone ? `text-${tone}` : undefined}>{value}</h4>
    </div>
)

// Totals and the monthly chart use the Accounting view, the one GL actuals are booked in.
// A vendor or product view compares its plan with the Acumatica bills linked to it in the Mapping tab.
const SummaryTab = ({ budget }: SummaryTabProps) => {
    const [planVsActual, setPlanVsActual] = useState<PlanVsActual | null>(null)
    const [selection, setSelection] = useState(ALL)

    useEffect(() => {
        getExpenseBudgetPlanVsActual(budget.id, PlanView.Accounting).then(setPlanVsActual)
    }, [budget.id])

    const planLines = useMemo(() => planVsActual?.costCodes.flatMap(c => c.lines) ?? [], [planVsActual])

    const totals = useMemo(() => {
        if (!planVsActual) return null
        const isClosed = (period: number) => period < planVsActual.firstOpenPeriod
        const monthly = planVsActual.periods.map((period, i) => ({
            month: calendar.periodToMonth(period % 100, "en-CA", true),
            planned: sumAmounts(planVsActual.costCodes.map(c => c.planned[i])),
            billed: billedOf(planVsActual, planLines, i),
            actual: isClosed(period) ? sumAmounts(planVsActual.costCodes.map(c => c.actual[i])) : null,
        }))
        const closedMonths = monthly.filter(m => m.actual !== null)
        return {
            monthly,
            planned: sumAmounts(monthly.map(m => m.planned)),
            plannedToDate: sumAmounts(closedMonths.map(m => m.planned)),
            actualToDate: sumAmounts(closedMonths.map(m => m.actual ?? 0)),
            billedToDate: sumAmounts(monthly.map(m => m.billed ?? 0)),
            forecast: sumAmounts(planVsActual.costCodes.map(c => c.forecast)),
        }
    }, [planVsActual, planLines])

    const vendorTotals = useMemo(() => {
        const byVendor = new Map<string, { planned: number; billed: number }>()
        planLines.forEach(line => {
            const total = byVendor.get(line.vendorName) ?? { planned: 0, billed: 0 }
            byVendor.set(line.vendorName, {
                planned: total.planned + sumAmounts(line.planned),
                billed: total.billed + sumAmounts(line.billed),
            })
        })
        return Array.from(byVendor, ([vendor, total]) => ({ vendor, planned: sumAmounts([total.planned]), billed: sumAmounts([total.billed]) }))
            .sort((a, b) => b.planned - a.planned)
    }, [planLines])

    const selectionOptions = useMemo(() => [
        { value: ALL, label: "All vendors and products" },
        ...vendorTotals.map(v => ({ value: `${VENDOR_PREFIX}${v.vendor}`, label: `${v.vendor} — all products` })),
        ...planLines.map(line => ({ value: `${LINE_PREFIX}${line.id}`, label: `${line.vendorName} · ${line.productName}` })),
    ], [vendorTotals, planLines])

    // Monthly plan of the selected vendor or product vs the Acumatica bills linked to it.
    const selectedMonthly = useMemo(() => {
        if (!totals || !planVsActual || selection === ALL) return totals?.monthly ?? []
        const selectedLines = selection.startsWith(VENDOR_PREFIX)
            ? planLines.filter(line => line.vendorName === selection.slice(VENDOR_PREFIX.length))
            : planLines.filter(line => line.id === selection.slice(LINE_PREFIX.length))
        return totals.monthly.map((month, i) => ({
            month: month.month,
            planned: sumAmounts(selectedLines.map(line => line.planned[i])),
            billed: billedOf(planVsActual, selectedLines, i),
        }))
    }, [totals, planVsActual, planLines, selection])

    if (!totals) return <LoadingState title="Loading Summary" subtitle="Comparing plan and actuals..." />

    const approved = Number(budget.approvedAmount)
    const varianceToDate = totals.actualToDate - totals.plannedToDate

    return (
        <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-wrap gap-3">
                <SummaryCard label="Approved" value={formatCurrency(approved)} />
                <SummaryCard label="Planned (year)" value={formatCurrency(totals.planned)}
                    tone={approved > 0 && totals.planned > approved ? "danger" : undefined} />
                <SummaryCard label="Billed to date" value={formatCurrency(totals.billedToDate)} />
                <SummaryCard label="Bills not linked" value={formatCurrency(planVsActual?.unlinkedBilled ?? 0)}
                    tone={planVsActual?.unlinkedBilled ? "danger" : "success"} />
                <SummaryCard label="GL actual to date" value={formatCurrency(totals.actualToDate)} />
                <SummaryCard label="Variance to date" value={formatCurrency(varianceToDate)}
                    tone={varianceToDate > 0 ? "danger" : "success"} />
                <SummaryCard label="Forecast (year)" value={formatCurrency(totals.forecast)}
                    tone={approved > 0 && totals.forecast > approved ? "danger" : "success"} />
            </div>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                <h5 className="mb-0">Monthly plan vs actual</h5>
                <SearchSelect containerStyle={{ minWidth: 320 }} options={selectionOptions} value={selection}
                    onChange={option => setSelection(option?.value ?? ALL)} />
            </div>
            <span className="text-muted">
                Billed = Acumatica bills linked to the products in the Mapping tab.
                {selection === ALL && " GL actual = everything booked to the cost code accounts (all vendors), shown once the month is closed."}
            </span>
            <ExpenseBudgetChart data={selectedMonthly} showActual={selection === ALL} />
            <h5 className="mb-0">Plan vs billed by vendor (year)</h5>
            {vendorTotals.length === 0
                ? <span className="text-muted">No products yet.</span>
                : <ExpenseBudgetVendorChart data={vendorTotals} />}
        </div>
    )
}

export default SummaryTab
